/**
 * Locale-aware date, time and number formatting.
 *
 * Every user-visible date, time and number goes through here so that it
 * honours the user's locale (`getIntlLocale()`: `de-DE`, `de-CH`, `fr-CH`,
 * `en-AU`, …) and the 12h / 24h preference from General settings. A German
 * user sees `17.09.2026`, `14:05` and `1,42 m/s`; a Swiss user sees `1'234.5`
 * (de-CH groups with an apostrophe and keeps a point decimal; the exact
 * apostrophe codepoint is ICU data and differs between Node builds, so never
 * assert one).
 *
 * `Intl.DateTimeFormat` / `Intl.NumberFormat` construction is expensive, and
 * several callers run inside render loops and canvas draw paths (echo caliper
 * labels, microscopy rulers, overlay text), so formatter instances are
 * memoised per locale + options. Keying by the locale string means a locale
 * change simply resolves to a fresh entry.
 *
 * NOT for machine-readable output: anything sent to the API, persisted,
 * embedded in DICOM strings, used as a cache key, compared as a string, fed
 * back into arithmetic, or written to a CSS/SVG attribute keeps `toFixed` /
 * `toISOString`.
 */
import { GENERAL_SETTINGS_STORAGE_KEY } from '@/components/settings/generalSettingsStorage';
import { getIntlLocale } from '@/lib/i18n';
// ---------------------------------------------------------------------------
// Preference access
// ---------------------------------------------------------------------------
/**
 * The user's explicit 12h/24h choice from General settings, or `null` when
 * they have never saved one. A missing preference deliberately falls back to
 * the locale's own convention (en-US → 12h, de-DE / en-GB → 24h) rather than
 * the settings form's `'12h'` default, which would force "2:05 PM" onto every
 * German user who has not opened the settings page.
 */
export function getTimeFormatPreference() {
    try {
        const raw = localStorage.getItem(GENERAL_SETTINGS_STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return parsed.timeFormat === '12h' || parsed.timeFormat === '24h' ? parsed.timeFormat : null;
    }
    catch {
        return null;
    }
}
function hourCycleForPreference() {
    const pref = getTimeFormatPreference();
    if (pref === '12h')
        return 'h12';
    if (pref === '24h')
        return 'h23';
    return undefined;
}
// ---------------------------------------------------------------------------
// Date normalisation
// ---------------------------------------------------------------------------
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DICOM_DATE_RE = /^(\d{4})(\d{2})(\d{2})(?:\d{6}(?:\.\d+)?)?$/;
/** Build a LOCAL calendar date, rejecting impossible dates (e.g. 2026-02-30). */
function localDate(year, month, day) {
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day)
        return null;
    return d;
}
/**
 * Normalise any accepted input to a valid `Date`, or `null`.
 *
 * A date-only ISO string (`2026-09-17`) is parsed as a LOCAL calendar date.
 * `new Date('2026-09-17')` would be UTC midnight, which every timezone west of
 * Greenwich renders as the 16th — the classic off-by-one on study dates.
 */
function toDate(value) {
    if (value == null || value === '')
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    const s = value.trim();
    const dateOnly = DATE_ONLY_RE.exec(s);
    if (dateOnly)
        return localDate(+dateOnly[1], +dateOnly[2], +dateOnly[3]);
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}
// ---------------------------------------------------------------------------
// Memoised formatter instances
// ---------------------------------------------------------------------------
const dateTimeFormatCache = new Map();
const numberFormatCache = new Map();
function getDateTimeFormat(locale, options) {
    const key = `${locale}|${JSON.stringify(options)}`;
    let fmt = dateTimeFormatCache.get(key);
    if (!fmt) {
        try {
            fmt = new Intl.DateTimeFormat(locale, options);
        }
        catch {
            // An unknown locale tag (or an invalid option combination) must never
            // take a whole screen down; fall back to the runtime default locale.
            fmt = new Intl.DateTimeFormat(undefined, options);
        }
        dateTimeFormatCache.set(key, fmt);
    }
    return fmt;
}
function getNumberFormat(locale, options, key) {
    let fmt = numberFormatCache.get(key);
    if (!fmt) {
        try {
            fmt = new Intl.NumberFormat(locale, options);
        }
        catch {
            fmt = new Intl.NumberFormat(undefined, options);
        }
        numberFormatCache.set(key, fmt);
    }
    return fmt;
}
/** Whether the options render an hour, i.e. whether the 12h/24h preference applies. */
function rendersHour(options) {
    return options.timeStyle !== undefined || options.hour !== undefined;
}
function formatWith(value, options) {
    const d = toDate(value);
    if (!d)
        return '';
    let resolved = options;
    if (rendersHour(options) && options.hour12 === undefined && options.hourCycle === undefined) {
        const hourCycle = hourCycleForPreference();
        if (hourCycle)
            resolved = { ...options, hourCycle };
    }
    return getDateTimeFormat(getIntlLocale(), resolved).format(d);
}
// ---------------------------------------------------------------------------
// Dates and times
// ---------------------------------------------------------------------------
const DATE_DEFAULTS = { dateStyle: 'medium' };
const DATE_TIME_DEFAULTS = { dateStyle: 'medium', timeStyle: 'short' };
const TIME_DEFAULTS = { timeStyle: 'short' };
/**
 * The compact all-numeric date: `9/17/2026` (en-US), `17.9.2026` (de-DE),
 * `17/09/2026` (en-AU / fr-FR). This is exactly what a bare
 * `toLocaleDateString()` produced, and dense surfaces (worklist rows, pickers,
 * table cells) pass it so the codemod localised those dates without also
 * widening the column. Note `dateStyle: 'short'` is NOT the same thing — it
 * abbreviates the year to `9/17/26`.
 */
export const NUMERIC_DATE = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
};
/**
 * `NUMERIC_DATE` plus a clock with seconds: what a bare `toLocaleString()`
 * produced (`9/17/2026, 2:05:33 PM`). Unlike the bare call, the hour cycle
 * still follows the user's 12h/24h setting.
 */
export const NUMERIC_DATE_TIME = {
    ...NUMERIC_DATE,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
};
/**
 * Date only. Default is the locale's medium style: `Sep 17, 2026` (en-US),
 * `17 Sept 2026` (en-GB / en-AU), `17.09.2026` (de-DE / de-CH),
 * `17 sept. 2026` (fr-FR). Pass `options` to use another shape, e.g.
 * `{ dateStyle: 'long' }` or `{ month: 'short', day: 'numeric' }`; they
 * replace the defaults rather than merge (Intl forbids mixing `dateStyle`
 * with component fields). Returns '' for null / empty / invalid input.
 */
export function formatDate(value, options) {
    return formatWith(value, options ?? DATE_DEFAULTS);
}
/**
 * Date + time: `Sep 17, 2026, 2:05 PM` (en-US) / `17.09.2026, 14:05` (de-DE).
 * The hour cycle follows the user's 12h/24h setting when they have one.
 */
export function formatDateTime(value, options) {
    return formatWith(value, options ?? DATE_TIME_DEFAULTS);
}
/** Time only: `2:05 PM` (en-US, 12h) / `14:05` (de-DE or 24h preference). */
export function formatTime(value, options) {
    return formatWith(value, options ?? TIME_DEFAULTS);
}
/**
 * A DICOM date (`YYYYMMDD`, optionally followed by a time) or an ISO date
 * (`YYYY-MM-DD` / full timestamp) shown to the user. Date-only inputs are
 * calendar dates and are never shifted by the timezone. Anything that cannot
 * be parsed is returned unchanged so the reader still sees the raw tag value.
 */
export function formatDicomDate(value, options) {
    if (!value)
        return '';
    const s = value.trim();
    const dicom = DICOM_DATE_RE.exec(s);
    const d = dicom ? localDate(+dicom[1], +dicom[2], +dicom[3]) : toDate(s);
    if (!d)
        return s;
    return formatDate(d, options);
}
const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/;
/**
 * A zone-less wall clock (`2026-09-17T14:05:33`, as a DICOM ContentDate +
 * ContentTime is) shown exactly as written: `Sep 17, 2026, 2:05 PM` on every
 * machine, whatever its timezone. `new Date()` would read the string in the
 * browser's zone (or as UTC with a trailing `Z`) and a reader west of the
 * device would see the previous day. The components are pinned to UTC on both
 * sides, so neither the browser's zone nor a cached formatter can shift them;
 * any zone designator after the clock is ignored for the same reason. A
 * date-only value formats as a date. The hour cycle follows the 12h/24h
 * setting. Returns '' for null / empty, and the raw text when it is no wall
 * clock at all.
 */
export function formatWallClockDateTime(value, options) {
    if (!value)
        return '';
    const s = value.trim();
    const m = WALL_CLOCK_RE.exec(s);
    if (!m)
        return s;
    const [year, month, day] = [+m[1], +m[2], +m[3]];
    const hasClock = m[4] !== undefined;
    const date = new Date(Date.UTC(year, month - 1, day, hasClock ? +m[4] : 0, hasClock ? +m[5] : 0, +(m[6] ?? 0)));
    if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day)
        return s;
    const shape = options ?? (hasClock ? DATE_TIME_DEFAULTS : DATE_DEFAULTS);
    return formatWith(date, { ...shape, timeZone: 'UTC' });
}
// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------
function isFormattable(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
/** `toFixed(0)` prints -0 as "0"; keep that so Intl does not surface "-0". */
function normaliseZero(value) {
    return Object.is(value, -0) ? 0 : value;
}
function clampDigits(fractionDigits) {
    return Math.min(20, Math.max(0, Math.trunc(fractionDigits)));
}
/**
 * General number formatting on the user's locale (grouping, decimal
 * separator). Returns '' for null / undefined / NaN / Infinity.
 */
export function formatNumber(value, options) {
    if (!isFormattable(value))
        return '';
    const locale = getIntlLocale();
    const opts = options ?? {};
    return getNumberFormat(locale, opts, `${locale}|n|${JSON.stringify(opts)}`).format(normaliseZero(value));
}
/**
 * Round exactly as `toFixed` does before handing the value to Intl.
 *
 * `Intl.NumberFormat` rounds the SHORTEST decimal representation of a double
 * (so 1.005 → "1.01"), while `toFixed` rounds its exact binary value (1.005 is
 * 1.00499999… → "1.00"). Every `.toFixed` call this module replaces was pinned
 * by tests and by the shared echo parity corpus on the `toFixed` digits, so
 * the digits are produced by `toFixed` and Intl only supplies the locale's
 * separators and grouping. `Number("1.00")` round-trips to a double whose
 * shortest form is "1", which Intl pads back out to the requested digits.
 */
function roundLikeToFixed(value, digits) {
    return Number(value.toFixed(digits));
}
/**
 * Fixed fraction digits with locale separators: the locale-aware stand-in for
 * `value.toFixed(fractionDigits)`. `formatDecimal(1.42, 2)` is `1.42` (en-US),
 * `1,42` (de-DE / fr-FR); `formatDecimal(1234.5, 1)` is `1,234.5` (en-US),
 * `1.234,5` (de-DE), `1'234.5` (de-CH). The digits are identical to
 * `toFixed`'s (see `roundLikeToFixed`). Returns `fallback` (default '') for
 * null / undefined / NaN / Infinity.
 */
export function formatDecimal(value, fractionDigits, fallback = '') {
    if (!isFormattable(value))
        return fallback;
    const digits = clampDigits(fractionDigits);
    const locale = getIntlLocale();
    return getNumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }, `${locale}|d|${digits}`).format(roundLikeToFixed(value, digits));
}
/**
 * A measured value with its unit symbol: `1.42 m/s` (en-US), `1,42 m/s`
 * (de-DE). Unit symbols (mm, cm, m/s, mmHg, HU, kg …) are locale-invariant by
 * clinical convention; only the number is localised. A plain space separates
 * the two so labels remain greppable and testable. Returns `fallback`
 * (default '—') when there is no value.
 */
export function formatMeasurement(value, unit, fractionDigits, fallback = '—') {
    if (!isFormattable(value))
        return fallback;
    const number = formatDecimal(value, fractionDigits);
    return unit ? `${number} ${unit}` : number;
}
/**
 * A percentage that is already scaled to 0–100 (`12.5` → `12.5%` in en-US,
 * `12,5 %` in de-DE). Returns `fallback` (default '') for a missing value.
 */
export function formatPercent(value, fractionDigits = 0, fallback = '') {
    if (!isFormattable(value))
        return fallback;
    const digits = clampDigits(fractionDigits);
    const locale = getIntlLocale();
    return getNumberFormat(locale, { style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits }, `${locale}|p|${digits}`).format(roundLikeToFixed(value, digits) / 100);
}
/**
 * A monetary amount in major units with an ISO 4217 code: `$12.50` (en-US),
 * `12,50 $` (fr-FR), `CHF 12.50` (de-CH). An unknown currency code falls back
 * to `12.50 XYZ` rather than throwing. Returns '' for a missing amount.
 */
export function formatCurrency(amount, currency) {
    if (!isFormattable(amount))
        return '';
    const code = currency.trim().toUpperCase();
    const locale = getIntlLocale();
    const key = `${locale}|c|${code}`;
    let fmt = numberFormatCache.get(key);
    if (!fmt) {
        try {
            fmt = new Intl.NumberFormat(locale, { style: 'currency', currency: code });
        }
        catch {
            return `${formatDecimal(amount, 2)} ${code}`.trim();
        }
        numberFormatCache.set(key, fmt);
    }
    return fmt.format(normaliseZero(amount));
}
